import type {
  CreateAnyPageData,
  CreateLessonData,
  CreateSectionData,
  ExportData,
  ExportedLesson,
  ExportedSection,
  ImportOptions,
  ImportResult,
} from './learningTypes';
import { adminLearningService } from './learningService';

export class ExportImportService {
  private static instance: ExportImportService;

  static getInstance(): ExportImportService {
    if (!ExportImportService.instance) {
      ExportImportService.instance = new ExportImportService();
    }
    return ExportImportService.instance;
  }

  // ==================== EXPORT METHODS ====================

  async exportSection(sectionId: string): Promise<ExportData> {
    try {
      const section = await adminLearningService.getSection(sectionId);
      if (!section) {
        throw new Error(`Section with ID ${sectionId} not found`);
      }

      const lessons = await adminLearningService.getLessonsInSection(sectionId);
      const exportedLessons: ExportedLesson[] = [];

      for (const lesson of lessons) {
        const pages = await adminLearningService.getPagesInLesson(sectionId, lesson.uid);
        exportedLessons.push({
          lesson,
          pages,
        });
      }

      const exportedSection: ExportedSection = {
        section: {
          uid: section.uid,
          title: section.title,
          subtitle: section.subtitle,
          imageSrc: section.imageSrc,
          order: section.order,
          status: section.status,
          isSupportOnly: section.isSupportOnly,
        },
        lessons: exportedLessons,
      };

      return {
        version: '1.0',
        exportDate: new Date().toISOString(),
        type: 'section',
        data: exportedSection,
      };
    } catch (error) {
      console.error('Error exporting section:', error);
      throw error;
    }
  }

  async exportLessons(sectionId: string, lessonIds: string[]): Promise<ExportData> {
    try {
      const exportedLessons: ExportedLesson[] = [];

      for (const lessonId of lessonIds) {
        const lesson = await adminLearningService.getLesson(sectionId, lessonId);
        if (!lesson) {
          throw new Error(`Lesson with ID ${lessonId} not found in section ${sectionId}`);
        }

        const pages = await adminLearningService.getPagesInLesson(sectionId, lessonId);
        exportedLessons.push({
          lesson,
          pages,
        });
      }

      return {
        version: '1.0',
        exportDate: new Date().toISOString(),
        type: 'lessons',
        data: exportedLessons,
      };
    } catch (error) {
      console.error('Error exporting lessons:', error);
      throw error;
    }
  }

  // ==================== IMPORT METHODS ====================

  async importData(exportData: ExportData, options: ImportOptions): Promise<ImportResult> {
    try {
      this.validateExportData(exportData);

      const result: ImportResult = {
        success: false,
        message: '',
        importedIds: {
          sectionIds: [],
          lessonIds: [],
          pageIds: [],
        },
        errors: [],
      };

      if (exportData.type === 'section') {
        return await this.importSection(exportData.data as ExportedSection, options, result);
      } else {
        return await this.importLessons(exportData.data as ExportedLesson[], options, result);
      }
    } catch (error) {
      console.error('Error importing data:', error);
      return {
        success: false,
        message: `Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        importedIds: { sectionIds: [], lessonIds: [], pageIds: [] },
        errors: [error instanceof Error ? error.message : 'Unknown error'],
      };
    }
  }

  private async importSection(
    exportedSection: ExportedSection,
    options: ImportOptions,
    result: ImportResult
  ): Promise<ImportResult> {
    try {
      const sectionData: CreateSectionData = {
        id: options.preserveIds ? exportedSection.section.uid : undefined,
        title: exportedSection.section.title,
        subtitle: exportedSection.section.subtitle,
        imageSrc: exportedSection.section.imageSrc,
        status: exportedSection.section.status,
        isSupportOnly: exportedSection.section.isSupportOnly,
        order: exportedSection.section.order,
      };

      let sectionId: string;
      if (options.preserveIds && options.overwriteExisting) {
        const existingSection = await adminLearningService.getSection(exportedSection.section.uid);
        if (existingSection) {
          await adminLearningService.updateSection(exportedSection.section.uid, sectionData);
          sectionId = exportedSection.section.uid;
        } else {
          sectionId = await adminLearningService.createSection(sectionData);
        }
      } else {
        sectionId = await adminLearningService.createSection(sectionData);
      }

      result.importedIds.sectionIds.push(sectionId);

      await this.importLessonsIntoSection(sectionId, exportedSection.lessons, options, result);

      result.success = true;
      result.message = `Successfully imported section "${exportedSection.section.title}" with ${exportedSection.lessons.length} lessons`;

      return result;
    } catch (error) {
      result.errors.push(`Failed to import section: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  }

  private async importLessons(
    exportedLessons: ExportedLesson[],
    options: ImportOptions,
    result: ImportResult
  ): Promise<ImportResult> {
    try {
      if (!options.targetSectionId) {
        throw new Error('Target section ID is required when importing lessons');
      }

      await this.importLessonsIntoSection(options.targetSectionId, exportedLessons, options, result);

      result.success = true;
      result.message = `Successfully imported ${exportedLessons.length} lessons`;

      return result;
    } catch (error) {
      result.errors.push(`Failed to import lessons: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  }

  private async importLessonsIntoSection(
    sectionId: string,
    exportedLessons: ExportedLesson[],
    options: ImportOptions,
    result: ImportResult
  ): Promise<void> {
    for (const exportedLesson of exportedLessons) {
      try {
        const lessonData: CreateLessonData = {
          id: options.preserveIds ? exportedLesson.lesson.uid : undefined,
          title: exportedLesson.lesson.title,
          imageSrc: exportedLesson.lesson.imageSrc,
          description: exportedLesson.lesson.description,
          isFree: exportedLesson.lesson.isFree,
          timeMinutes: exportedLesson.lesson.timeMinutes,
          type: exportedLesson.lesson.type,
          status: exportedLesson.lesson.status,
          order: exportedLesson.lesson.order || 0,
          color: exportedLesson.lesson.color,
        };

        let lessonId: string;
        if (options.preserveIds && options.overwriteExisting) {
          const existingLesson = await adminLearningService.getLesson(sectionId, exportedLesson.lesson.uid);
          if (existingLesson) {
            await adminLearningService.updateLesson(sectionId, exportedLesson.lesson.uid, lessonData);
            lessonId = exportedLesson.lesson.uid;
          } else {
            lessonId = await adminLearningService.createLesson(sectionId, lessonData);
          }
        } else {
          lessonId = await adminLearningService.createLesson(sectionId, lessonData);
        }

        result.importedIds.lessonIds.push(lessonId);

        for (const page of exportedLesson.pages) {
          try {
            let pageData: CreateAnyPageData;

            if (page.type === 'question') {
              pageData = {
                uid: options.preserveIds ? page.uid : undefined,
                content: page.content,
                order: page.order,
                buttonText: page.buttonText,
                type: 'question',
                aiFriendlyQuestion: page.aiFriendlyQuestion || '',
                options: page.options,
                correctOptionIds: page.correctOptionIds,
                correctAnswerReaction: page.correctAnswerReaction,
                incorrectAnswerReaction: page.incorrectAnswerReaction,
                shouldSaveAnswer: page.shouldSaveAnswer,
              };
            } else {
              pageData = {
                uid: options.preserveIds ? page.uid : undefined,
                content: page.content,
                order: page.order,
                buttonText: page.buttonText,
                type: 'page',
              };
            }

            let pageId: string;
            if (options.preserveIds && options.overwriteExisting && page.uid) {
              const existingPages = await adminLearningService.getPagesInLesson(sectionId, lessonId);
              const existingPage = existingPages.find((p) => p.uid === page.uid);
              if (existingPage) {
                await adminLearningService.updatePage(sectionId, lessonId, page.uid, pageData);
                pageId = page.uid;
              } else {
                pageId = await adminLearningService.createPage(sectionId, lessonId, pageData);
              }
            } else {
              pageId = await adminLearningService.createPage(sectionId, lessonId, pageData);
            }

            result.importedIds.pageIds.push(pageId);
          } catch (error) {
            result.errors.push(
              `Failed to import page in lesson "${exportedLesson.lesson.title}": ${error instanceof Error ? error.message : 'Unknown error'}`
            );
          }
        }
      } catch (error) {
        result.errors.push(`Failed to import lesson "${exportedLesson.lesson.title}": ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  }

  async getAllSectionsFromCollection(): Promise<ExportedSection[]> {
    try {
      const sections = await adminLearningService.getAllSections();
      const exportedSections: ExportedSection[] = [];

      for (const section of sections) {
        const lessons = await adminLearningService.getLessonsInSection(section.uid);
        const exportedLessons: ExportedLesson[] = [];

        for (const lesson of lessons) {
          const pages = await adminLearningService.getPagesInLesson(section.uid, lesson.uid);
          exportedLessons.push({
            lesson,
            pages,
          });
        }

        exportedSections.push({
          section: {
            uid: section.uid,
            title: section.title,
            subtitle: section.subtitle,
            imageSrc: section.imageSrc,
            order: section.order,
            status: section.status,
            isSupportOnly: section.isSupportOnly,
          },
          lessons: exportedLessons,
        });
      }

      return exportedSections;
    } catch (error) {
      console.error('Error fetching sections from collection:', error);
      throw error;
    }
  }

  // ==================== VALIDATION ====================

  private validateExportData(exportData: ExportData): void {
    if (!exportData || typeof exportData !== 'object') {
      throw new Error('Invalid export data format');
    }

    if (!exportData.version || !exportData.exportDate || !exportData.type || !exportData.data) {
      throw new Error('Export data is missing required fields');
    }

    if (!['section', 'lessons'].includes(exportData.type)) {
      throw new Error('Invalid export type. Must be "section" or "lessons"');
    }

    if (exportData.type === 'section') {
      this.validateSectionData(exportData.data as ExportedSection);
    } else {
      this.validateLessonsData(exportData.data as ExportedLesson[]);
    }
  }

  private validateSectionData(sectionData: ExportedSection): void {
    if (!sectionData.section || !sectionData.section.title) {
      throw new Error('Section data is missing required fields');
    }

    if (!Array.isArray(sectionData.lessons)) {
      throw new Error('Section lessons must be an array');
    }

    this.validateLessonsData(sectionData.lessons);
  }

  private validateLessonsData(lessonsData: ExportedLesson[]): void {
    if (!Array.isArray(lessonsData)) {
      throw new Error('Lessons data must be an array');
    }

    for (const exportedLesson of lessonsData) {
      if (!exportedLesson.lesson || !exportedLesson.lesson.title) {
        throw new Error('Lesson data is missing required fields');
      }

      if (!Array.isArray(exportedLesson.pages)) {
        throw new Error('Lesson pages must be an array');
      }

      for (const page of exportedLesson.pages) {
        if (!page.content || typeof page.order !== 'number' || !page.type) {
          throw new Error('Page data is missing required fields');
        }
      }
    }
  }

  // ==================== TEXT FORMAT EXPORT ====================

  convertToTextFormat(exportData: ExportData): string {
    const lessons: ExportedLesson[] =
      exportData.type === 'section'
        ? (exportData.data as ExportedSection).lessons
        : (exportData.data as ExportedLesson[]);

    const lessonBlocks = lessons.map(({ lesson, pages }) => {
      const sortedPages = [...pages].sort((a, b) => a.order - b.order);

      const titleLine = `<title># ${lesson.title}</title>`;

      const pageLines = sortedPages.map((page) => {
        const contentBlock = `<page>${page.content}</page>`;

        if (page.type === 'question') {
          const optionLines = page.options
            .map((opt) => `<option>${opt.text}</option>`)
            .join('\n');
          return `${contentBlock}\n\n${optionLines}\n\n<button>${page.buttonText}</button>`;
        }

        return `${contentBlock}\n\n<button>${page.buttonText}</button>`;
      });

      return [titleLine, ...pageLines].join('\n\n');
    });

    return lessonBlocks.join('\n\n---\n\n');
  }

  downloadAsTextFile(exportData: ExportData, filename?: string): void {
    const textContent = this.convertToTextFormat(exportData);
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename || this.generateTextFilename(exportData);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private generateTextFilename(exportData: ExportData): string {
    const date = new Date().toISOString().split('T')[0];

    if (exportData.type === 'section') {
      const sectionData = exportData.data as ExportedSection;
      const sectionTitle = sectionData.section.title.replace(/[^a-zA-Z0-9]/g, '_');
      return `${sectionTitle}_section_${date}.txt`;
    } else {
      const lessonsData = exportData.data as ExportedLesson[];
      return `${lessonsData.length}_lessons_${date}.txt`;
    }
  }

  // ==================== UTILITY METHODS ====================

  downloadAsFile(exportData: ExportData, filename?: string): void {
    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename || this.generateFilename(exportData);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private generateFilename(exportData: ExportData): string {
    const date = new Date().toISOString().split('T')[0];
    const type = exportData.type;

    if (type === 'section') {
      const sectionData = exportData.data as ExportedSection;
      const sectionTitle = sectionData.section.title.replace(/[^a-zA-Z0-9]/g, '_');
      return `${sectionTitle}_section_${date}.json`;
    } else {
      const lessonsData = exportData.data as ExportedLesson[];
      return `${lessonsData.length}_lessons_${date}.json`;
    }
  }

  async parseImportFile(file: File): Promise<ExportData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const jsonString = event.target?.result as string;
          const exportData = JSON.parse(jsonString) as ExportData;
          resolve(exportData);
        } catch {
          reject(new Error('Invalid JSON file format'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }
}

export const exportImportService = ExportImportService.getInstance();
